import * as THREE from 'three';
import { POLYGONS, RENDER_LAYERS } from "../Global/variables";
import { createSegmentPlane } from "../Global/functions";
import {
  applyBackFacePattern,
  createWireframe,
  getObstacleBoundaryPoints,
  getEdgeDirection,
  getPanelTiltLiftOffset,
  polygonsOverlapOnPlane,
  resolvePanelTransform,
} from './Helpers';
import { checkPanelsOK } from './PanelFunctions';
import inside from 'point-in-polygon';
import { pushUndoAction } from "../Global/UndoManager";
import { isPolygonInsidePolygon } from '../Files/Calculations/DormerIdentification';
import { getPanelImage } from "../../helpers/panelImageCache";

// basic function that automatically populates the segment with panels
// populates simply with selected options of potrait or landsacpe  
export async function autoPopulateSegment(
  segmentIndex,
  solarPanel,
  addPanel,
  panelList,
  scene,
  camera,
  setPanelsOK,
  arePanelsOK,
  options = {},
  stateHelpers = {}
) {

  const { panelSpacing = 1/39.37 } = options; // Default 1 inch in meters
  const { setPolygons = null, uiPanelsSnapshot = null } = stateHelpers;

  // Get segment plane and shape
  const plane = createSegmentPlane(segmentIndex);
  const segment = POLYGONS[segmentIndex].shape;
  const segmentVertices = getSegmentVertices(segmentIndex);

  // Get panel dimensions based on selected panel
  const data = panelList.filter((panel) => panel.id === solarPanel.id)[0];
  let panelWidth, panelHeight, area;
  
  if (data.unit === 'Metric') {
    // Convert mm to meters
    panelWidth = data.width / 1000;
    panelHeight = data.height / 1000;
    area = data.width * data.height / 1000000; // Area in square meters
  } else if (data.unit === 'Imperial') {
    // Convert inches to meters
    panelWidth = data.width / 39.37;
    panelHeight = data.height / 39.37;
    area = data.width * data.height / 1549.9969; // Area in square meters
  }
  
  // Swap dimensions if landscape orientation
  if (solarPanel.orientation === "Landscape") {
    [panelWidth, panelHeight] = [panelHeight, panelWidth];
  }

  // Calculate optimal spacing between panels (use the same spacing as in the application)
  const OFFSET = panelSpacing; // Spacing between panels in meters
  const spacedPanelWidth = panelWidth + OFFSET;
  const spacedPanelHeight = panelHeight + OFFSET;

  // Get segment orientation
  const normal = plane.normal.clone().normalize();
  const edgeDirection = getEdgeDirection(POLYGONS[segmentIndex], normal, 'horizontal');
  
  const insetMargin = Math.max(
    panelSpacing * 0.5,
    Math.min(panelWidth, panelHeight) * 0.08
  );
  const insetVertices = shrinkPolygon(segmentVertices, insetMargin);
 
  const basePositions = generateBestOptimizedPlacements(
    insetVertices,
    spacedPanelWidth,
    spacedPanelHeight,
    normal,
    edgeDirection,
    plane,
    segmentIndex
  );

  // Second pass: add gap-anchored panels to fill residual space around the
  // primary grid without enlarging the required module gap.
  const fillPositions = fillResidualPlacements(
    insetVertices,
    spacedPanelWidth,
    spacedPanelHeight,
    normal,
    edgeDirection,
    segmentIndex,
    basePositions
  );

  const positions = [...basePositions, ...fillPositions];
  
  let stats = {
    totalPositions: positions.length,
    panelsPlaced: 0,
    failedPlacements: 0
  };

  // Snapshot existing panels for undo/redo
  const prevPanels = Array.isArray(POLYGONS[segmentIndex].panels)
    ? POLYGONS[segmentIndex].panels.map((p) => ({ panel: p.panel, area: p.area, id: p.id }))
    : [];
  const prevUiPanels = Array.isArray(uiPanelsSnapshot)
    ? uiPanelsSnapshot.map((p) => ({ ...p }))
    : null;

  const newPanels = [];
  const newUiPanels = [];

  const detachPanel = (panel) => {
    if (!panel) return;
    if (panel.parent) panel.parent.remove(panel);
    else if (scene) scene.remove(panel);
  };

  const attachPanel = (panel) => {
    if (!panel) return;
    if (!panel.parent && scene) scene.add(panel);
  };

  const setUIPanels = (panelsList) => {
    if (typeof setPolygons !== "function") return;
    setPolygons((prev) => {
      if (!prev || !prev[segmentIndex]) return prev;
      const next = [...prev];
      const seg = { ...next[segmentIndex] };
      seg.panels = Array.isArray(panelsList) ? panelsList.map((p) => ({ ...p })) : [];
      next[segmentIndex] = seg;
      return next;
    });
  };

  // Clear existing panels in the segment
  if (POLYGONS[segmentIndex].panels.length > 0) {
      POLYGONS[segmentIndex].panels.forEach(panelData => {
          if (panelData.panel) {
              detachPanel(panelData.panel);
          }
      });
      POLYGONS[segmentIndex].panels = [];
      if (typeof setPolygons === "function") {
        setUIPanels([]);
      }
  }

  // Try to place panels at each position
  for (const position of positions) {
    try {
      // Create panel at this position
      const panel = await createPanelAtPosition(
        position,
        panelWidth,
        panelHeight,
        area,
        solarPanel,
        data,
        scene,
        plane,
        segment,
        normal,
        edgeDirection,
        segmentIndex
      );           
        
        // Panel is valid, add it to the segment
        POLYGONS[segmentIndex].panels.push({panel: panel, area: area, id: solarPanel.id});
        
        // Add panel to UI list
        const panelTransform = panel?.userData?.panelTransform || {};
        let uiData = {
          ...solarPanel,
          panel_id: panel.id,
          tilt: panelTransform.tilt ?? Number(solarPanel?.tilt || 0),
          rotation: panelTransform.rotation ?? Number(solarPanel?.rotation || 0),
          azimuth: panelTransform.azimuth ?? Number(solarPanel?.azimuth || 0),
        };
        addPanel({ newPanel: uiData, segmentIndex: segmentIndex });

        newPanels.push({ panel, area, id: solarPanel.id });
        newUiPanels.push({ ...uiData });
        
        
        stats.panelsPlaced++;
      
    } catch (error) {
      console.error("Error placing panel:", error);
      stats.failedPlacements++;
    }
  }

  // Validate all panels after placement
  checkPanelsOK(segmentIndex, setPanelsOK, arePanelsOK);
  const horizontalAxis = edgeDirection.clone().normalize();
  const verticalAxis = new THREE.Vector3().crossVectors(normal, horizontalAxis).normalize();

  for (const panel of POLYGONS[segmentIndex].panels) {
    const corners = calculatePanelCorners(panel.panel.position, panelWidth, panelHeight, horizontalAxis, verticalAxis);

    if (!areAllPanelCornersInsideSegment(
        corners,
        segmentVertices,
        panel.panel.position,
        horizontalAxis,
        verticalAxis
      )) {
      scene.remove(panel.panel)
    }
  }

  if (prevPanels.length || newPanels.length) {
    pushUndoAction({
      label: "Auto fill panels",
      undo: () => {
        // remove new panels
        newPanels.forEach((p) => detachPanel(p.panel));
        // restore previous panels
        prevPanels.forEach((p) => attachPanel(p.panel));

        POLYGONS[segmentIndex].panels = prevPanels.map((p) => ({ ...p }));
        if (prevUiPanels) {
          setUIPanels(prevUiPanels);
        } else if (typeof setPolygons === "function") {
          setUIPanels([]);
        }

        if (typeof setPanelsOK === "function") {
          checkPanelsOK(segmentIndex, setPanelsOK, arePanelsOK);
        }
      },
      redo: () => {
        // remove previous panels
        prevPanels.forEach((p) => detachPanel(p.panel));
        // restore new panels
        newPanels.forEach((p) => attachPanel(p.panel));

        POLYGONS[segmentIndex].panels = newPanels.map((p) => ({ ...p }));
        if (newUiPanels.length && typeof setPolygons === "function") {
          setUIPanels(newUiPanels);
        }

        if (typeof setPanelsOK === "function") {
          checkPanelsOK(segmentIndex, setPanelsOK, arePanelsOK);
        }
      },
    });
  }

  return stats;
}

function areAllPanelCornersInsideSegment(
  panelCorners,
  segmentVertices,
  origin,
  horizontalAxis,
  verticalAxis
) {
  // Create normalized basis vectors in the polygon's plane
  const u = horizontalAxis.clone().normalize();
  const v = verticalAxis.clone().normalize();

  // Project a 3D point to 2D coordinates in the polygon's plane
  function projectToPlane2D(point) {
    const relative = point.clone().sub(origin);
    return {
      x: relative.dot(u),
      y: relative.dot(v),
    };
  }

  // Perform 2D point-in-polygon check using ray casting
  function isPointInPolygon(point, polygon) {
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const xi = polygon[i].x, yi = polygon[i].y;
      const xj = polygon[j].x, yj = polygon[j].y;

      const intersect =
        (yi > point.y) !== (yj > point.y) &&
        point.x < ((xj - xi) * (point.y - yi)) / ((yj - yi) || 1e-10) + xi;

      if (intersect) inside = !inside;
    }
    return inside;
  }

  // Project all segment vertices to 2D
  const projectedSegment = segmentVertices.map(projectToPlane2D);

  // Check each corner
  for (const corner of panelCorners) {
    const projectedCorner = projectToPlane2D(corner);
    const isInside = isPointInPolygon(projectedCorner, projectedSegment);

    if (!isInside) {
      return false;
    }
  }

  return true;
}

function shrinkPolygon(vertices, margin = 0.1) {
  const centroid = calculateCentroid(vertices);
  return vertices.map(v => {
    const dir = new THREE.Vector3().subVectors(v, centroid).normalize();
    return new THREE.Vector3().subVectors(v, dir.multiplyScalar(margin));
  });
}

function getSegmentVertices(segmentIndex) {
    const segment = POLYGONS[segmentIndex];
    
    if (segment.edges && segment.edges.length > 0) {
        const vertices = [];
        const edges = segment.setBacks.isSet  ? segment.setBacks.edgesData : segment.edges;
        
        vertices.push(edges[0].point1);
        
        for (let i = 1; i < edges.length; i++) {
            const currentEdge = edges[i];
            vertices.push(currentEdge.point1);
        }
        
        return vertices;
    }
}

function generatePlacementsFromOffset(
  segmentVertices,
  panelWidth,
  panelHeight,
  normal,
  edgeDirection,
  plane,
  segmentIndex,
  offset = { x: 0, y: 0 }
) {
  const horizontalAxis = edgeDirection.clone().normalize();
  const verticalAxis = new THREE.Vector3().crossVectors(normal, horizontalAxis).normalize();

  const centroid = calculateCentroid(segmentVertices);
  const projectedCentroid = new THREE.Vector3();
  plane.projectPoint(centroid, projectedCentroid);

  let maxDistance = 0;
  segmentVertices.forEach(vertex => {
    const distance = projectedCentroid.distanceTo(vertex);
    maxDistance = Math.max(maxDistance, distance);
  });

  const buffer = 1.2;
  const estimatedRows = Math.ceil((maxDistance * 2 * buffer) / panelHeight);
  const estimatedCols = Math.ceil((maxDistance * 2 * buffer) / panelWidth);

  const startPosition = new THREE.Vector3().copy(projectedCentroid)
    .sub(horizontalAxis.clone().multiplyScalar((estimatedCols * panelWidth) / 2))
    .sub(verticalAxis.clone().multiplyScalar((estimatedRows * panelHeight) / 2))
    .add(horizontalAxis.clone().multiplyScalar(offset.x))
    .add(verticalAxis.clone().multiplyScalar(offset.y));

  const validPositions = [];

  for (let row = 0; row < estimatedRows; row++) {
    for (let col = 0; col < estimatedCols; col++) {
      const positionCenter = new THREE.Vector3().copy(startPosition)
        .add(horizontalAxis.clone().multiplyScalar(col * panelWidth + panelWidth / 2))
        .add(verticalAxis.clone().multiplyScalar(row * panelHeight + panelHeight / 2));

      const projectedPosition = new THREE.Vector3();
      plane.projectPoint(positionCenter, projectedPosition);

      const corners = calculatePanelCorners(
        projectedPosition, 
        panelWidth, 
        panelHeight,
        horizontalAxis,
        verticalAxis
      ); 

      let obstructionOverlap = false;
      for (const obstruction of POLYGONS[segmentIndex]?.obstructions || []) {
        if (checkPanelOverlap(corners, obstruction, normal)) {
          obstructionOverlap = true;
          break;
        }
      }

      if (isPanelCompletelyInside(
        projectedPosition,
        panelWidth,
        panelHeight,
        horizontalAxis,
        verticalAxis,
        segmentVertices,
        normal,
        segmentIndex
      )) {
        validPositions.push(projectedPosition);
      }
    }
  }
  return validPositions;
}

function generateBestOptimizedPlacements(
  segmentVertices,
  panelWidth,
  panelHeight,
  normal,
  edgeDirection,
  plane,
  segmentIndex
) {

  const attempts = generateOffsets(panelWidth, panelHeight, 0.40); // Step = 25% of panel size

  let bestPositions = [];
  for (const offset of attempts) {
    const positions = generatePlacementsFromOffset(
      segmentVertices,
      panelWidth,
      panelHeight,
      normal,
      edgeDirection,
      plane,
      segmentIndex,
      offset
    );

    if (positions.length > bestPositions.length) {
      bestPositions = positions;
      
    }
  }

  return bestPositions;
}

function generateOffsets(panelWidth, panelHeight, step = 0.25) {
  const offsets = [];
  const offsetSteps = [-step, 0, step];

  for (const dx of offsetSteps) {
    for (const dy of offsetSteps) {
      offsets.push({ x: dx * panelWidth, y: dy * panelHeight });
    }
  }

  return offsets;
}

function fillResidualPlacements(
  segmentVertices,
  panelWidth,
  panelHeight,
  normal,
  edgeDirection,
  segmentIndex,
  seedPositions = []
) {
  const horizontalAxis = edgeDirection.clone().normalize();
  const verticalAxis = new THREE.Vector3().crossVectors(normal, horizontalAxis).normalize();
  // panelWidth/panelHeight are the *cell* dimensions: physical module size
  // plus the required module gap. Keep every residual panel exactly one cell
  // away from a neighbouring panel on one axis. The perpendicular offset lets
  // a panel step up/down to follow an irregular roof edge without turning the
  // required one-inch gap into an arbitrary larger gap.
  const accepted = seedPositions.map((position) => ({ position }));

  const extras = [];
  const perpendicularOffsets = [-0.5, -0.33, -0.25, -0.16, 0, 0.16, 0.25, 0.33, 0.5];
  const tolerance = 1e-6;

  const hasRequiredClearance = (position) =>
    accepted.every(({ position: acceptedPosition }) => {
      const delta = position.clone().sub(acceptedPosition);
      const horizontalDistance = Math.abs(delta.dot(horizontalAxis));
      const verticalDistance = Math.abs(delta.dot(verticalAxis));

      // Expanded footprints may touch at their edges: that represents the
      // intended physical module gap. They may never overlap.
      return horizontalDistance >= panelWidth - tolerance ||
        verticalDistance >= panelHeight - tolerance;
    });

  const isInsideSegmentAndClear = (position) =>
    hasRequiredClearance(position) && isPanelCompletelyInside(
      position,
      panelWidth,
      panelHeight,
      horizontalAxis,
      verticalAxis,
      segmentVertices,
      normal,
      segmentIndex
    );

  // Start at every normal-grid panel, then let newly accepted panels seed the
  // next step. This can fill a narrow, offset roof section while preserving a
  // one-cell (one-inch-gap) relationship at every placement.
  for (let anchorIndex = 0; anchorIndex < accepted.length; anchorIndex++) {
    const anchor = accepted[anchorIndex].position;
    const candidates = [];

    for (const perpendicularOffset of perpendicularOffsets) {
      candidates.push(
        anchor.clone()
          .add(horizontalAxis.clone().multiplyScalar(panelWidth))
          .add(verticalAxis.clone().multiplyScalar(perpendicularOffset * panelHeight)),
        anchor.clone()
          .add(horizontalAxis.clone().multiplyScalar(-panelWidth))
          .add(verticalAxis.clone().multiplyScalar(perpendicularOffset * panelHeight)),
        anchor.clone()
          .add(verticalAxis.clone().multiplyScalar(panelHeight))
          .add(horizontalAxis.clone().multiplyScalar(perpendicularOffset * panelWidth)),
        anchor.clone()
          .add(verticalAxis.clone().multiplyScalar(-panelHeight))
          .add(horizontalAxis.clone().multiplyScalar(perpendicularOffset * panelWidth))
      );
    }

    for (const position of candidates) {
      if (!isInsideSegmentAndClear(position)) continue;
      accepted.push({ position });
      extras.push(position);
    }
  }

  return extras;
}

function checkPanelOverlap(panelCorners, obstruction, normal) {
  if (panelCorners.length !== 4 || !obstruction) return false;
  const footprints = [];
  const offsetBoundary = getObstacleBoundaryPoints(obstruction);
  if (Array.isArray(offsetBoundary) && offsetBoundary.length >= 3) {
    footprints.push(offsetBoundary);
  }
  const baseBoundary = Array.isArray(obstruction?.cords) ? obstruction.cords : [];
  if (baseBoundary.length >= 3) {
    footprints.push(baseBoundary);
  }
  if (!footprints.length) return false;

  return footprints.some((footprint) =>
    polygonsOverlapOnPlane(panelCorners, footprint, normal, panelCorners[0])
  );
}


function calculateCentroid(vertices) {
  const centroid = new THREE.Vector3(0, 0, 0);
  
  for (let i = 0; i < vertices.length; i++) {
    centroid.add(vertices[i]);
  }
  
  centroid.divideScalar(vertices.length);
  return centroid;
}

function calculatePanelCorners(center, width, height, horizontalAxis, verticalAxis) {
  const halfWidth = width / 2;
  const halfHeight = height / 2;
  
  const topLeft = new THREE.Vector3().copy(center)
    .sub(horizontalAxis.clone().multiplyScalar(halfWidth))
    .sub(verticalAxis.clone().multiplyScalar(halfHeight));
    
  const topRight = new THREE.Vector3().copy(center)
    .add(horizontalAxis.clone().multiplyScalar(halfWidth))
    .sub(verticalAxis.clone().multiplyScalar(halfHeight));
    
  const bottomRight = new THREE.Vector3().copy(center)
    .add(horizontalAxis.clone().multiplyScalar(halfWidth))
    .add(verticalAxis.clone().multiplyScalar(halfHeight));
    
  const bottomLeft = new THREE.Vector3().copy(center)
    .sub(horizontalAxis.clone().multiplyScalar(halfWidth))
    .add(verticalAxis.clone().multiplyScalar(halfHeight));
  
  return [topLeft, topRight, bottomRight, bottomLeft];
}

function isPointInPolygon(point, polygonVertices, normal, buffer = 0.05) {
  const absNormal = new THREE.Vector3(
    Math.abs(normal.x),
    Math.abs(normal.y),
    Math.abs(normal.z)
  );

  let projectFunction;

  if (absNormal.x >= absNormal.y && absNormal.x >= absNormal.z) {
    projectFunction = (v) => [v.y, v.z]; // YZ
  } else if (absNormal.y >= absNormal.x && absNormal.y >= absNormal.z) {
    projectFunction = (v) => [v.x, v.z]; // XZ
  } else {
    projectFunction = (v) => [v.x, v.y]; // XY
  }

  const projectedPoint = projectFunction(point);
  const projectedPolygon = polygonVertices.map(projectFunction);

  // 1. Calculate the centroid of the polygon
  const centroid = projectedPolygon.reduce(
    (acc, [x, y]) => [acc[0] + x, acc[1] + y],
    [0, 0]
  ).map(coord => coord / projectedPolygon.length);

  // 2. Shrink the polygon slightly toward the centroid
  const shrunkenPolygon = projectedPolygon.map(([x, y]) => {
    const dx = x - centroid[0];
    const dy = y - centroid[1];
    const length = Math.sqrt(dx * dx + dy * dy);
    if (length === 0) return [x, y]; // Skip if it's the centroid
    const factor = (length - buffer) / length;
    return [
      centroid[0] + dx * factor,
      centroid[1] + dy * factor
    ];
  });

  // 3. Perform point-in-polygon check using 'inside' lib
  return inside(projectedPoint, shrunkenPolygon);
}

function createPanelAtPosition(
  position,
  width,
  height,
  area,
  solarPanel,
  panelData,
  scene,
  plane,
  segment,
  normal,
  edgeDirection,
  segmentIndex
) {
  return new Promise((resolve, reject) => {
    try {
      // Create panel geometry
      const geometry = new THREE.PlaneGeometry(width, height);
      
      // Calculate direction
      const direction = edgeDirection;
      
      // Keep a fixed clearance + configured panel base height above the segment.
      const clearanceOffset = 0.01; // 1cm clearance above the roof surface
      const baseHeightM = (solarPanel?.height || 0) / 39.37;
      const baseOffsetM = clearanceOffset + baseHeightM;
      
      // Load texture for the panel
      const textureLoader = new THREE.TextureLoader();
      textureLoader.load(
        getPanelImage(panelData.id),
        (texture) => {
          // Set texture properties
          texture.wrapS = THREE.RepeatWrapping;
          texture.wrapT = THREE.RepeatWrapping;
          texture.repeat.set(1, 1);
          
          if (solarPanel.orientation === "Landscape") {
            texture.rotation = Math.PI / 2;
          }
          
          // Create panel material and mesh
          const material = new THREE.MeshStandardMaterial({ 
            map: texture, 
            roughness: 0.8, 
            side: THREE.DoubleSide, 
            depthTest: false, 
            depthWrite: false, 
            transparent: true 
          });
          applyBackFacePattern(material);
          
          const panel = new THREE.Mesh(geometry, material);
          panel.name = "panel";
          panel.renderOrder = RENDER_LAYERS.PANEL;
          panel.frustumCulled = false;
          
          // Set panel type based on orientation
          if (solarPanel.orientation === "Portrait") {
            panel.panelType = 'potrait';
          } else {
            panel.panelType = 'landscape';
          }
          
          const transform = resolvePanelTransform(plane.normal, direction, solarPanel);
          panel.userData.panelTransform = {
            tilt: transform.tilt,
            rotation: transform.rotation,
            azimuth: transform.azimuth,
            segmentIndex,
          };
          panel.quaternion.copy(transform.quaternion);
          const orientation = transform.orientation.clone();
          const tiltLiftM = getPanelTiltLiftOffset(height, transform.tilt);
          const centerOffsetM = baseOffsetM + tiltLiftM;

          // Position using base + tilt center offset so panel never sinks into the segment.
          panel.position.copy(
            position.clone().add(
              orientation.clone().multiplyScalar(centerOffsetM)
            )
          );
          
          // Create wireframe
          createWireframe(panel);
          
          // Add to scene
          scene.add(panel);
          
          resolve(panel);
        },
        undefined,
        (error) => {
          console.error('Error loading texture:', error);
          resolve(null);
        }
      );
    } catch (error) {
      console.error("Error creating panel:", error);
      resolve(null);
    }
  });
}

function isPanelCompletelyInside(
  panelCenter,
  panelWidth,
  panelHeight,
  horizontalAxis,
  verticalAxis,
  segmentVertices,
  normal,
  segmentIndex
) {
  const corners = calculatePanelCorners(panelCenter, panelWidth, panelHeight, horizontalAxis, verticalAxis);
  
  const pointsToCheck = [
    ...corners,
    panelCenter,
    ...samplePanelEdgePoints(corners, 50),
    ...samplePanelInteriorPoints(corners, 4)
  ];  

  // Check every point
  for (const point of pointsToCheck) {
    if (!isPointInPolygon(point, segmentVertices, normal)) {
      return false;
    }
  }
  
  // Check obstructions
  for (const obstruction of POLYGONS[segmentIndex]?.obstructions || []) {
    if (checkPanelOverlap(corners, obstruction, normal)) {
      return false;
    }
  }

  // Check dormers — separate segments whose footprint sits inside this one.
  // Panels must not be auto-placed over them.
  if (overlapsDormer(corners, segmentIndex, normal)) {
    return false;
  }

  return true;
}

/**
 * Returns a segment's full outline (boundary edges) as world-space points.
 *
 * @param {number} segmentIndex - Index of the segment in POLYGONS
 * @returns {Array<THREE.Vector3>} Outline points, or [] when unavailable
 */
function getSegmentOutlineWorld(segmentIndex) {
  const segment = POLYGONS[segmentIndex];
  if (!Array.isArray(segment?.edges) || segment.edges.length < 3) return [];
  return segment.edges.map((edge) => edge?.point1).filter(Boolean);
}

/**
 * Checks if a panel footprint overlaps a dormer of the given segment.
 *
 * A dormer is simply another segment whose footprint lies fully inside the
 * target segment (same containment test the DXF pipeline uses). Such segments
 * are independent, so panels must not be placed over them.
 *
 * @param {Array<THREE.Vector3>} panelCorners - The panel's 4 corner points
 * @param {number} segmentIndex - Index of the segment being populated
 * @param {THREE.Vector3} normal - Normal vector of the segment plane
 * @returns {boolean} True if the panel overlaps a contained dormer
 */
function overlapsDormer(panelCorners, segmentIndex, normal) {
  const parentOutline = getSegmentOutlineWorld(segmentIndex);
  if (parentOutline.length < 3) return false;

  for (let i = 0; i < POLYGONS.length; i++) {
    if (i === segmentIndex) continue;

    const candidateOutline = getSegmentOutlineWorld(i);
    if (candidateOutline.length < 3) continue;

    // Only segments fully inside the target segment count as dormers.
    if (!isPolygonInsidePolygon(candidateOutline, parentOutline)) continue;

    if (polygonsOverlapOnPlane(panelCorners, candidateOutline, normal, panelCorners[0])) {
      return true;
    }
  }

  return false;
}

function samplePanelEdgePoints(corners, samplesPerEdge = 5) {
  const edgePoints = [];
  
  // Sample points along each of the 4 edges
  for (let i = 0; i < corners.length; i++) {
    const start = corners[i];
    const end = corners[(i + 1) % corners.length];
    
    // Sample points along this edge (excluding start/end as they're already corners)
    for (let j = 1; j < samplesPerEdge; j++) {
      const t = j / samplesPerEdge; // Parameter from 0 to 1
      const point = new THREE.Vector3().lerpVectors(start, end, t);
      edgePoints.push(point);
    }
  }
  
  return edgePoints;
}

function samplePanelInteriorPoints(corners, gridSize = 3) {
  const interiorPoints = [];
  
  // Create a grid of points inside the panel
  for (let i = 1; i < gridSize; i++) {
    for (let j = 1; j < gridSize; j++) {
      const u = i / gridSize; // 0 to 1
      const v = j / gridSize; // 0 to 1
      
      // Bilinear interpolation within the panel
      const top = new THREE.Vector3().lerpVectors(corners[0], corners[1], u);
      const bottom = new THREE.Vector3().lerpVectors(corners[3], corners[2], u);
      const point = new THREE.Vector3().lerpVectors(top, bottom, v);
      
      interiorPoints.push(point);
    }
  }
  
  return interiorPoints;
}
